import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { KeyRound, Home, TrendingUp, ArrowRight, Settings } from "lucide-react";

const TYPES = [
  {
    key: "first_home_buyer",
    icon: KeyRound,
    title: "First Home Buyer",
    line: "Your first step onto the ladder — grants, budget and the home to start in.",
    accent: "First step",
  },
  {
    key: "home_buyer",
    icon: Home,
    title: "Home Buyer",
    line: "Your next home — discovered around the life you already love.",
    accent: "Next home",
  },
  {
    key: "investor",
    icon: TrendingUp,
    title: "Investor",
    line: "Build wealth — a property planned around yield, growth and your tenants.",
    accent: "Build wealth",
  },
];

export default function Landing() {
  const { setClientType } = useJourney();
  const navigate = useNavigate();

  return (
    <StageFrame testId="stage-landing" className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
      <button
        data-testid="admin-link"
        onClick={() => navigate("/admin")}
        className="hotspot fixed top-4 right-5 z-40 flex items-center gap-2 text-xs text-white/80 hover:text-white transition-colors"
      >
        <Settings size={14} /> Admin
      </button>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="text-center max-w-2xl">
        <Eyebrow className="justify-center">AI Property Decision Engine</Eyebrow>
        <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-forest mt-4 leading-[1.02]">
          Who are we building<br />with today?
        </h1>
        <p className="text-mutedText mt-5 max-w-lg mx-auto">
          Choose the journey that fits your client. We'll tailor the conversation, the numbers and the outcome around them.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 w-full max-w-5xl">
        {TYPES.map((t, i) => (
          <motion.button
            key={t.key}
            data-testid={`landing-type-${t.key}`}
            onClick={() => setClientType(t.key)}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.1 }}
            whileHover={{ y: -6 }}
            className="group relative text-left rounded-3xl border border-line bg-white/[0.03] p-7 transition-colors hover:border-brand/50 hover:bg-white/[0.06]"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-brand/15 text-brand">
              <t.icon size={22} />
            </div>
            <span className="label-eyebrow text-brand mt-5 block">{t.accent}</span>
            <h2 className="font-serif text-2xl text-forest mt-1">{t.title}</h2>
            <p className="text-sm text-mutedText mt-2 leading-relaxed">{t.line}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-forest/80 group-hover:text-brand transition-colors">
              Begin <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </span>
          </motion.button>
        ))}
      </div>
    </StageFrame>
  );
}
